import { nowUTC } from "@homebound/activesupport";
import {
  actionColumn,
  beamEnvironmentBannerLayoutHeightVar,
  Breadcrumb,
  collapseColumn,
  column,
  Css,
  dateColumn,
  emptyCell,
  GridColumn,
  GridDataRow,
  GridTableLayout,
  HeaderAction,
  IconButton,
  PageHeaderLayout,
  RowStyles,
  simpleHeader,
  Tag,
  TagGroup,
  Tooltip,
  useGridTableLayoutState,
  useModal,
} from "@homebound/beam";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { dateCell } from "src/components";
import { cycleTrackingAction } from "src/components/CycleTrackingAction";
import { StatusIndicator } from "src/components/StatusIndicator";
import {
  BidPackageBidderKind,
  CostCodeBidSummaryFlag,
  CostCodeBidSummaryStatus,
  ReadyPlanStatus,
  SaveCommunityInput,
} from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { FormattedBidName } from "src/routes/bids/BidPackagesPage";
import { useDeleteBidPackageMutation } from "src/routes/bids/BidPackagesPage.generated";
import { BidPackageStatusesModal } from "src/routes/bids/components/BidPackageStatusesModal";
import { ConfirmationModal } from "src/routes/components/ConfirmationModal";
import { createHierarchyBreadcrumbs } from "src/routes/projects/ProjectHeader";
import { ProjectParams } from "src/routes/routesDef";
import { createBidPackagesUrl, createBidPackagesV2Url } from "src/RouteUrls";
import { extractPoMessages, queryResult } from "src/utils";
import {
  BidPackagesPageV2_BidPackageFragment,
  BidPackagesPageV2MetadataQuery,
  BidPackagesPageV2Query,
  useBidPackagesPageV2MetadataQuery,
  useBidPackagesPageV2Query,
  useSaveBiddingCycleMutation,
} from "./BidPackagesPageV2.generated";
import { CostCodeCoverageSummary } from "./CostCodeCoverageSummary";
import { BidPackagesPageFilter, createFilterDefs, mapToFilter, matchesBidCoverage } from "./filters";
import { CostCodeBidSummaryBanners } from "./flagBanners";
import {
  BID_PACKAGES_PAGE_LIMIT,
  bidPackageCreateOptions,
  bidPackageHref,
  CostCodeBidSummaryGroup,
  estimateSourceTag,
  groupCostCodeBidSummaries,
  statusTag,
} from "./utils";

export function BidPackagesPageV2() {
  const { projectId } = useRouteParams<ProjectParams>();
  const query = useBidPackagesPageV2MetadataQuery({
    variables: { projectId, page: { limit: BID_PACKAGES_PAGE_LIMIT } },
  });
  return queryResult(query, {
    data: ({ community, bidPackages, enumDetails, costCodes }) => (
      <BidPackagesPageV2View
        projectId={projectId}
        community={community}
        bidPackages={bidPackages.entities}
        bidPackageStatuses={enumDetails.bidPackageStatus}
        costCodes={costCodes}
      />
    ),
  });
}

type BidPackagesPageV2ViewProps = {
  projectId: string;
  community: BidPackagesPageV2MetadataQuery["community"];
  bidPackages: BidPackagesPageV2MetadataQuery["bidPackages"]["entities"];
  bidPackageStatuses: BidPackagesPageV2MetadataQuery["enumDetails"]["bidPackageStatus"];
  costCodes: BidPackagesPageV2MetadataQuery["costCodes"];
};

function BidPackagesPageV2View({
  projectId,
  community,
  bidPackages,
  bidPackageStatuses,
  costCodes,
}: BidPackagesPageV2ViewProps) {
  const { name } = community;
  const filterDefs = useMemo(
    () => createFilterDefs(community, bidPackageStatuses, costCodes),
    [community, bidPackageStatuses, costCodes],
  );
  const layoutState = useGridTableLayoutState<BidPackagesPageFilter>({
    persistedFilter: { filterDefs, storageKey: `${projectId}_bidPackagesFilter` },
    search: "client",
  });
  const { filter, setFilter } = layoutState;
  const tableRef = useRef<HTMLDivElement>(null);
  const pendingScrollBidCoverage = useRef<BidPackagesPageFilter["bidCoverage"]>(undefined);
  const warningCoverage = [CostCodeBidSummaryFlag.CostMissingScope, CostCodeBidSummaryFlag.MissingTrade];
  function applyBidCoverageFilter(bidCoverage: BidPackagesPageFilter["bidCoverage"]) {
    pendingScrollBidCoverage.current = bidCoverage;
    setFilter({ bidCoverage });
  }
  const query = useBidPackagesPageV2Query({
    variables: {
      filter: mapToFilter(filter, projectId),
      bidPackagesFilter: filter.status?.nonEmpty ? { status: filter.status } : undefined,
      page: { limit: BID_PACKAGES_PAGE_LIMIT },
    },
  });
  // Defer until the filtered rows have rendered, rather than moving the viewport while the old grid is still visible.
  useEffect(() => {
    const pendingBidCoverage = pendingScrollBidCoverage.current;
    if (!pendingBidCoverage) return;
    // Clear Filters (or any other grid filter change) cancels a pending summary-triggered scroll.
    if (!matchesPendingBidCoverage(filter.bidCoverage, pendingBidCoverage)) {
      pendingScrollBidCoverage.current = undefined;
      return;
    }
    if (query.loading) return;
    const animationFrame = requestAnimationFrame(() => {
      pendingScrollBidCoverage.current = undefined;
      tableRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start", inline: "end" });
    });
    return () => cancelAnimationFrame(animationFrame);
  }, [filter.bidCoverage, query.data, query.loading]);
  const onDelete = useDeleteBidPackageConfirmation();
  const { openModal } = useModal();
  const onStatusHelp = useCallback(() => openModal({ content: <BidPackageStatusesModal />, size: "xl" }), [openModal]);
  const columns = useMemo(() => createColumns(onDelete, onStatusHelp), [onDelete, onStatusHelp]);
  const [saveBiddingCycle] = useSaveBiddingCycleMutation();

  const saveCycle = useCallback(
    (input: Pick<SaveCommunityInput, "biddingStartedAt" | "biddingCompletedAt">) =>
      saveBiddingCycle({ variables: { input: { id: projectId, ...input } } }),
    [saveBiddingCycle, projectId],
  );
  const rowStyles = useMemo(() => createRowStyles(projectId), [projectId]);
  const createRows = useCallback(
    (data: BidPackagesPageV2Query | undefined) => {
      const groups = groupCostCodeBidSummaries(data?.costCodeBidSummaries.entities ?? [], bidPackages);
      return createSummaryRows(
        groups.filter((g) => matchesBidCoverage(g, filter.bidCoverage)),
        projectId,
      );
    },
    [bidPackages, filter.bidCoverage, projectId],
  );
  // The hierarchy crumbs link to each level's bid packages, e.g. "Los Angeles / Altadena / Bid Packages"
  const breadcrumbs: Breadcrumb[] = [
    ...createHierarchyBreadcrumbs(community, createBidPackagesUrl),
    { label: "Bid Packages", href: createBidPackagesV2Url(projectId) },
  ];
  const actions: HeaderAction[] = [
    cycleTrackingAction({
      title: "Bidding",
      startedAt: community.biddingStartedAt?.toPlainDate(),
      completedAt: community.biddingCompletedAt?.toPlainDate(),
      onStart: () => saveCycle({ biddingStartedAt: nowUTC() }),
      onMarkComplete: () => saveCycle({ biddingCompletedAt: nowUTC() }),
      onReopen: () => saveCycle({ biddingCompletedAt: null }),
      onReset: () => saveCycle({ biddingStartedAt: null, biddingCompletedAt: null }),
    }),
    {
      kind: "menu",
      trigger: { label: "Create", variant: "primary" },
      items: bidPackageCreateOptions(projectId).map(({ label, url }) => ({ label, onClick: url })),
    },
  ];
  return (
    <PageHeaderLayout
      pageHeader={{ title: "Bid Packages", documentTitleSuffix: name, breadcrumbs: { breadcrumbs }, actions }}
    >
      <CostCodeCoverageSummary
        projectId={projectId}
        onIssuesClick={() =>
          applyBidCoverageFilter([
            CostCodeBidSummaryStatus.Missing,
            CostCodeBidSummaryStatus.Incomplete,
            ...warningCoverage,
          ])
        }
        onMissingClick={() => applyBidCoverageFilter([CostCodeBidSummaryStatus.Missing])}
        onIncompleteClick={() => applyBidCoverageFilter([CostCodeBidSummaryStatus.Incomplete])}
        onWarningsClick={() => applyBidCoverageFilter(warningCoverage)}
      />
      {/* scrollMarginTop is used to account for the environment banner height when visible
       * Beam ticket to address globally: https://linear.app/homebound-team/issue/DS-371/set-scroll-margins-on-body-based-on-stickyfixed-banners-and-headers
       */}
      <div
        ref={tableRef}
        css={Css.h0.wPx(1).add("scrollMarginTop", `var(${beamEnvironmentBannerLayoutHeightVar})`).$}
      />
      <GridTableLayout
        layoutState={layoutState}
        emptyFallback="There aren't any cost codes to display."
        tableProps={{ query, createRows, columns, rowStyles, sorting: { on: "client", initial: ["name", "ASC"] } }}
      />
    </PageHeaderLayout>
  );
}

type HeaderRow = { kind: "header" };
type SummaryRow = { kind: "summary"; data: CostCodeBidSummaryGroup };
type PackageRow = {
  kind: "package";
  data: { group: CostCodeBidSummaryGroup; bidPackage: BidPackagesPageV2_BidPackageFragment };
};
type Row = HeaderRow | SummaryRow | PackageRow;

function matchesPendingBidCoverage(
  currentBidCoverage: BidPackagesPageFilter["bidCoverage"],
  pendingBidCoverage: NonNullable<BidPackagesPageFilter["bidCoverage"]>,
) {
  return (
    currentBidCoverage?.length === pendingBidCoverage.length &&
    currentBidCoverage.every((coverage, index) => coverage === pendingBidCoverage[index])
  );
}

function createRowStyles(projectId: string): RowStyles<Row> {
  return {
    summary: {
      cellCss: ({ data: { status } }) =>
        status.code === CostCodeBidSummaryStatus.Missing
          ? Css.bgRed50.$
          : status.code === CostCodeBidSummaryStatus.Incomplete
            ? Css.bgOrange50.$
            : {},
    },
    // The whole row opens the bid package, or reopens the create flow if it's still a first draft, the delete
    // column is an actionColumn, so it stays clickable.
    package: { rowLink: ({ data }) => bidPackageHref(data.bidPackage, projectId) },
  };
}

function createSummaryRows(groups: CostCodeBidSummaryGroup[], projectId: string): GridDataRow<Row>[] {
  return [
    simpleHeader,
    ...groups.map((group) => ({
      kind: "summary" as const,
      id: group.id,
      data: group,
      // The flags stack under the summary row, also while it's collapsed.
      companion: () =>
        group.flags.nonEmpty ? <CostCodeBidSummaryBanners group={group} projectId={projectId} /> : null,
      // A bid package can sit under several groups (i.e. Labor and Materials), so its row id needs the group prefix
      children: group.bidPackages.map((bidPackage) => ({
        kind: "package" as const,
        id: `${group.id}_${bidPackage.id}`,
        data: { group, bidPackage },
      })),
    })),
  ];
}

function createColumns(
  onDelete: (bidPackage: BidPackagesPageV2_BidPackageFragment) => void,
  onStatusHelp: () => void,
): GridColumn<Row>[] {
  return [
    collapseColumn<Row>({ name: "Collapse", canHide: false }),
    column<Row>({
      id: "code",
      name: "Code",
      header: "Code",
      summary: ({ costCode }) => ({
        content: () => <div css={Css.xs.$}>{costCode.number}</div>,
        sortValue: costCode.number,
      }),
      package: ({ group }) => ({
        content: () => <div css={Css.xs.$}>{group.costCode.number}</div>,
        sortValue: group.costCode.number,
      }),
      w: "80px",
      mw: "80px",
    }),
    column<Row>({
      id: "name",
      name: "Name",
      canHide: false,
      header: "Name",
      w: 2,
      mw: "220px",
      // Names run long in prod, so they ellipsize to the column and the tooltip carries the full name
      summary: ({ costCode }) => ({
        content: () => (
          <Tooltip title={costCode.name}>
            <span css={Css.smSb.truncate.mw0.$}>{costCode.name}</span>
          </Tooltip>
        ),
        value: costCode.name,
      }),
      package: ({ group, bidPackage }) => ({
        content: () => (
          <div css={Css.df.fdr.aic.gap2.xs.mw0.$}>
            <Tooltip title={bidPackage.costCodesName}>
              <FormattedBidName
                costCodesName={bidPackage.costCodesName}
                latestVersion={bidPackage.latestVersion}
                isPlanBasedV2={bidPackage.isPlanBasedV2}
                truncate
              />
            </Tooltip>
            {bidPackage.isPlanBasedV2 && bidPackage.isMissingOptions && (
              <Tag text="Add Missing Options" icon="error" iconOnly type="warning" />
            )}
            {bidPackage.isMissingCosts && <Tag text="Missing Costs" icon="finances" iconOnly type="warning" />}
          </div>
        ),
        css: Css.sm.$,
        value: bidPackage.costCodesName,
        sortValue: group.costCode.name,
      }),
    }),
    column<Row>({
      id: "costType",
      name: "Cost Type",
      header: "Cost Type",
      summary: ({ costType }) => ({ content: costType.name, value: "" }),
      package: ({ bidPackage }) => ({ content: bidPackage.costType.name, value: "" }),
      mw: "100px",
      clientSideSort: false,
    }),
    column<Row>({
      id: "bidCoverage",
      name: "Bid Coverage",
      header: "Bid Coverage",
      summary: ({ status }) => <Tag text={status.name} {...statusTag(status.code)} />,
      package: emptyCell,
      w: "125px",
      mw: "125px",
      clientSideSort: false,
    }),
    column<Row>({
      id: "bidType",
      name: "Bid Type",
      header: "Bid Type",
      summary: emptyCell,
      package: ({ bidPackage: { isUnitBased } }) => ({ content: isUnitBased ? "Unit" : "Plan", value: "" }),
      w: "90px",
      mw: "90px",
      clientSideSort: false,
    }),
    column<Row>({
      id: "productOfferings",
      name: "Product Offerings",
      header: "Product Offerings",
      summary: emptyCell,
      package: ({ bidPackage }) => (
        <ProductOfferings latestVersion={bidPackage.latestVersion} projects={bidPackage.projects} />
      ),
      mw: "140px",
      clientSideSort: false,
    }),
    column<Row>({
      id: "hierarchy",
      name: "Hierarchy",
      header: "Hierarchy",
      summary: emptyCell,
      package: ({ bidPackage }) => <TagGroup tags={bidPackage.projects.map((p) => p.name).map((text) => ({ text }))} />,
      mw: "120px",
      clientSideSort: false,
    }),
    column<Row>({
      id: "status",
      name: "Status",
      header: "Status",
      tooltip: onStatusHelp,
      summary: emptyCell,
      package: ({ bidPackage: { status } }) => (
        <div css={Css.df.aic.$}>
          <StatusIndicator status={status.code} type="BidPackage" />
          <span css={Css.ml1.$}>{status.name}</span>
        </div>
      ),
      mw: "170px",
      clientSideSort: false,
    }),
    column<Row>({
      id: "source",
      name: "Source",
      header: "Source",
      summary: emptyCell,
      package: ({ bidPackage }) => ({
        content: <BidPackageSource bidders={bidPackage.activeBidders} />,
        value: bidPackageSourceSearchValue(bidPackage.activeBidders),
      }),
      w: "160px",
      mw: "160px",
      clientSideSort: false,
    }),
    dateColumn<Row>({
      id: "lastEdited",
      name: "Last Edited",
      header: "Last Edited",
      summary: emptyCell,
      package: ({ bidPackage }) => dateCell(bidPackage.updatedAt),
      mw: "110px",
      clientSideSort: false,
    }),
    actionColumn<Row>({
      id: "actions",
      name: "Actions",
      canHide: false,
      header: emptyCell,
      summary: emptyCell,
      package: ({ bidPackage }) => ({
        content: (
          <IconButton
            inc={2}
            icon="trash"
            onClick={() => onDelete(bidPackage)}
            disabled={extractPoMessages(bidPackage.canDelete) || !bidPackage.canDelete.allowed}
          />
        ),
      }),
      w: "80px",
      mw: "80px",
    }),
  ];
}

/** The product offerings on the bid's latest version out of the ones active at its scope. */
function ProductOfferings(props: Pick<BidPackagesPageV2_BidPackageFragment, "latestVersion" | "projects">) {
  const { latestVersion, projects } = props;
  const onBid = latestVersion.readyPlanAggregateVersions.length;
  const total =
    projects
      .flatMap((p) =>
        p.__typename === "Community"
          ? p.productOfferings
          : p.__typename === "Market"
            ? p.children.flatMap((c) => c.productOfferings)
            : [],
      )
      .filter((po) => po.status.code === ReadyPlanStatus.Active).length || onBid;
  if (total === 0) return null;
  return (
    <Tooltip
      title={
        <div css={Css.df.fdc.gap1.$}>
          {latestVersion.readyPlanAggregateVersions.map((rpav) => (
            <div key={rpav.id}>
              {rpav.readyPlan.name} {rpav.displayVersion}
            </div>
          ))}
        </div>
      }
      placement="top"
    >
      <span>{`${onBid === total ? "All " : ""}${onBid}/${total}`}</span>
    </Tooltip>
  );
}

type Bidder = BidPackagesPageV2_BidPackageFragment["activeBidders"][number];

/** Text used by client search for the Source column; mirrors what BidPackageSource renders. */
function bidPackageSourceSearchValue(bidders: Bidder[]): string {
  if (bidders.isEmpty) return "No bidders yet";
  return bidders
    .map((b) =>
      b.kind === BidPackageBidderKind.InternalEstimate
        ? (b.bidContract?.internalEstimateType?.name ?? "Internal Estimate")
        : b.name,
    )
    .join(" ");
}

/** Trades show their name, internal estimates their type. */
function BidPackageSource({ bidders }: { bidders: Bidder[] }) {
  if (bidders.isEmpty) return <span css={Css.gray700.$}>No bidders yet</span>;
  const [estimates, trades] = bidders.partition((b) => b.kind === BidPackageBidderKind.InternalEstimate);
  return (
    <div css={Css.df.fdc.gap1.$}>
      {estimates.map((e) => (
        <div key={e.id}>
          {/* Unit based internal estimates have no type, so keep the generic tag for them */}
          <Tag
            text={e.bidContract?.internalEstimateType?.name ?? "Internal Estimate"}
            {...estimateSourceTag(e.bidContract?.internalEstimateType?.code)}
          />
        </div>
      ))}
      {trades.map((t) => (
        <span key={t.id}>{t.name}</span>
      ))}
    </div>
  );
}

/** Confirms the delete, then refetches the grid. */
function useDeleteBidPackageConfirmation() {
  const { openModal } = useModal();
  const [deleteBidPackage] = useDeleteBidPackageMutation({
    refetchQueries: ["BidPackagesPageV2Metadata", "BidPackagesPageV2"],
  });
  return useCallback(
    (bidPackage: { id: string; name: string }) =>
      openModal({
        content: (
          <ConfirmationModal
            confirmationMessage={
              <>
                Are you sure you want to delete this bid package <b>{bidPackage.name}</b>? It will not be archived and
                cannot be undone.
              </>
            }
            onConfirmAction={async () => {
              await deleteBidPackage({ variables: { id: bidPackage.id } });
            }}
            title="Delete Bid Package"
            label="Yes, Delete"
          />
        ),
      }),
    [deleteBidPackage, openModal],
  );
}
